import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import axios from 'axios';
import { MessageCircle, Send, CheckCircle2, Clock, Search, Bell } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import AdminLayout from '@/components/AdminLayout';

const getBackendUrl = () => {
    if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.startsWith('http')) {
        return process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');
    }
    return '';
};

const getWsBaseUrl = () => {
    if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.startsWith('http')) {
        return process.env.REACT_APP_BACKEND_URL.replace('http://', 'ws://').replace('https://', 'wss://').replace(/\/api$/, '').replace(/\/$/, '');
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
};

const BACKEND_URL = getBackendUrl();
const WS_BASE_URL = getWsBaseUrl();

export const AdminChat = () => {
    const { user } = useApp();
    const [sessions, setSessions] = useState([]);
    const [activeSession, setActiveSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputMsg, setInputMsg] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [notifPermission, setNotifPermission] = useState(Notification.permission);
    const [sending, setSending] = useState(false);

    const ws = useRef(null);
    const messagesEndRef = useRef(null);
    const activeSessionRef = useRef(null);

    // Keep ref in sync with state so WS handler can read it without stale closure
    useEffect(() => { activeSessionRef.current = activeSession; }, [activeSession]);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
    };

    useEffect(() => {
        fetchSessions();
        connectAdminWebSocket();
        // Request browser notification permission
        if (Notification.permission === 'default') {
            Notification.requestPermission().then(p => setNotifPermission(p));
        }
        return () => { if (ws.current) ws.current.close(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { scrollToBottom(); }, [messages]);

    useEffect(() => {
        if (activeSession) {
            fetchSessionHistory(activeSession.id);
            setSessions(prev =>
                prev.map(s => s.id === activeSession.id ? { ...s, unread_admin: 0 } : s)
            );
        }
    }, [activeSession]);

    const authHeaders = () => {
        const token = localStorage.getItem('token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const fetchSessions = async () => {
        try {
            const { data } = await axios.get(`${BACKEND_URL}/api/chat/sessions`, { headers: authHeaders() });
            setSessions(data || []);
        } catch (e) {
            console.error('Failed to fetch chat sessions', e);
        }
    };

    const fetchSessionHistory = async (sessionId) => {
        try {
            const { data } = await axios.get(`${BACKEND_URL}/api/chat/sessions/${sessionId}`);
            setMessages(data.messages || []);
        } catch (e) {
            console.error('Failed to fetch session history', e);
        }
    };

    const showBrowserNotification = (senderName, content) => {
        if (notifPermission === 'granted' && document.hidden) {
            try {
                new Notification(`💬 ${senderName}`, {
                    body: content,
                    icon: '/logo.png',
                    tag: 'gpgt-chat'
                });
            } catch (e) { /* ignore */ }
        }
    };

    const connectAdminWebSocket = () => {
        if (ws.current) ws.current.close();

        const clientId = user?.id || Math.random().toString(36).substring(7);
        const token = localStorage.getItem('token') || '';
        const wsUrl = `${WS_BASE_URL}/api/chat/ws/admin/${clientId}?token=${encodeURIComponent(token)}`;

        const socket = new WebSocket(wsUrl);

        socket.onopen = () => { /* WebSocket connected — admin chat ready */ };

        socket.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);

                if (msg.type === 'new_session') {
                    setSessions(prev => [msg.data, ...prev]);
                    showBrowserNotification(msg.data.customer_name, 'New chat started');
                    return;
                }

                if (msg.type === 'presence' && msg.role === 'customer') {
                    setSessions(prev => prev.map(s => s.id === msg.session_id ? { ...s, is_online: msg.online } : s));
                    if (activeSessionRef.current?.id === msg.session_id) {
                        setActiveSession(prev => ({ ...prev, is_online: msg.online }));
                    }
                    return;
                }

                if (msg.type === 'session_message') {
                    const { session_id, data } = msg;

                    // Show browser notification for incoming customer messages
                    if (!data.is_admin) {
                        showBrowserNotification(data.customer_name || 'Customer', data.content);
                    }

                    setSessions(prev => prev.map(s => {
                        if (s.id === session_id) {
                            return {
                                ...s,
                                last_message: data.is_admin ? `You: ${data.content}` : data.content,
                                updated_at: data.timestamp,
                                unread_admin: activeSessionRef.current?.id === session_id ? 0 : (data.is_admin ? s.unread_admin || 0 : (s.unread_admin || 0) + 1)
                            };
                        }
                        return s;
                    }).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)));

                    // Append to active thread
                    if (activeSessionRef.current?.id === session_id) {
                        setMessages(prev => {
                            if (prev.find(m => m.id === data.id)) return prev;
                            
                            let currentMessages = [...prev];
                            if (data.is_admin) {
                                const localIdx = currentMessages.findIndex(m => m.id.startsWith('local_') && m.content === data.content);
                                if (localIdx !== -1) {
                                    currentMessages.splice(localIdx, 1);
                                }
                            }
                            return [...currentMessages, data];
                        });
                    }
                }
            } catch (e) {
                console.error('[AdminChat] Failed to parse WS message', e);
            }
        };

        socket.onclose = () => {
            // Auto-reconnect after 4 seconds
            setTimeout(connectAdminWebSocket, 4000);
        };

        ws.current = socket;
    };

    const sendAdminReply = async (e) => {
        e.preventDefault();
        const text = inputMsg.trim();
        if (!text || !activeSession || sending) return;

        setSending(true);
        setInputMsg('');

        const optimisticMsg = {
            id: 'local_' + Date.now(),
            session_id: activeSession.id,
            content: text,
            is_admin: true,
            timestamp: new Date().toISOString()
        };

        // Optimistic update — show immediately in admin UI
        setMessages(prev => [...prev, optimisticMsg]);

        const wsPayload = JSON.stringify({
            type: 'admin_reply',
            session_id: activeSession.id,
            content: text
        });

        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            ws.current.send(wsPayload);
        } else {
            // REST fallback if WS is down
            try {
                await axios.post(`${BACKEND_URL}/api/chat/sessions/${activeSession.id}/messages`, {
                    content: text,
                    is_admin: true
                }, { headers: authHeaders() });
            } catch (err) {
                console.error('[AdminChat] REST fallback failed', err);
            }
        }

        setSending(false);
    };

    const requestNotifPermission = async () => {
        const p = await Notification.requestPermission();
        setNotifPermission(p);
    };

    const filteredSessions = sessions.filter(s =>
        (s.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (s.customer_email || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalUnread = sessions.reduce((sum, s) => sum + (s.unread_admin || 0), 0);

    return (
        <AdminLayout title="Live Chat">
            <div className="flex h-[calc(100vh-8rem)] bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

                {/* LEFT PANEL: Sessions Sidebar */}
                <div className="w-1/3 min-w-[280px] border-r border-gray-100 flex flex-col bg-gray-50/50">
                    <div className="p-4 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-navy flex items-center gap-2">
                                <MessageCircle className="h-5 w-5 text-[#0c1b44]" />
                                Live Chat
                                {totalUnread > 0 && (
                                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{totalUnread}</span>
                                )}
                            </h2>
                            {notifPermission !== 'granted' && (
                                <button onClick={requestNotifPermission} title="Enable notifications" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                    <Bell className="h-3 w-3" /> Enable
                                </button>
                            )}
                        </div>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Search conversations..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 bg-white"
                            />
                        </div>
                    </div>

                    <ScrollArea className="flex-1">
                        {filteredSessions.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <MessageCircle className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                                <p>No active conversations found.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {filteredSessions.map((session) => (
                                    <button
                                        key={session.id}
                                        onClick={() => setActiveSession(session)}
                                        className={`w-full text-left p-4 hover:bg-white transition-colors flex items-start gap-3 ${activeSession?.id === session.id ? 'bg-white border-l-4 border-l-[#0c1b44]' : ''}`}
                                    >
                                        <div className="relative flex-shrink-0">
                                            <div className="h-10 w-10 rounded-full bg-[#0c1b44]/10 text-[#0c1b44] flex items-center justify-center font-bold">
                                                {(session.customer_name || '?').charAt(0).toUpperCase()}
                                            </div>
                                            {session.is_online && (
                                                <div className="absolute -bottom-1 -right-1 h-3.5 w-3.5 bg-green-500 border-2 border-white rounded-full"></div>
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-baseline mb-1">
                                                <h4 className="font-semibold text-gray-900 truncate pr-2">{session.customer_name}</h4>
                                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                                    {new Date(session.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                            <p className={`text-sm truncate ${session.unread_admin > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                                                {session.last_message}
                                            </p>
                                        </div>

                                        {session.unread_admin > 0 && activeSession?.id !== session.id && (
                                            <div className="bg-red-500 text-white text-[10px] font-bold h-5 min-w-[20px] rounded-full flex items-center justify-center px-1.5 flex-shrink-0">
                                                {session.unread_admin}
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </ScrollArea>
                </div>

                {/* RIGHT PANEL: Active Chat Window */}
                <div className="flex-1 flex flex-col bg-white">
                    {activeSession ? (
                        <>
                            {/* Header */}
                            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white shadow-sm z-10">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-[#0c1b44]/10 text-[#0c1b44] flex items-center justify-center font-bold">
                                        {activeSession.customer_name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900">{activeSession.customer_name}</h3>
                                        <p className="text-xs text-gray-500 flex items-center gap-1">
                                            {activeSession.is_online ? (
                                                <><div className="h-2 w-2 rounded-full bg-green-500" /> Online now</>
                                            ) : (
                                                <><div className="h-2 w-2 rounded-full bg-gray-300" /> Offline</>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-gray-600">{activeSession.customer_email}</p>
                                    <p className="text-xs text-gray-400 flex items-center gap-1 justify-end mt-0.5">
                                        <Clock className="h-3 w-3" />
                                        Started {new Date(activeSession.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>

                            {/* Chat History */}
                            <ScrollArea className="flex-1 p-6 bg-gray-50/30">
                                <div className="space-y-4 pb-4">
                                    <div className="flex justify-center">
                                        <span className="text-xs text-gray-400 bg-gray-100 border border-gray-200 px-3 py-1 rounded-full shadow-sm">
                                            {new Date(activeSession.created_at).toLocaleString()} — Chat Started
                                        </span>
                                    </div>

                                    {messages.map((msg) => (
                                        <div key={msg.id} className={`flex ${msg.is_admin ? 'justify-end' : 'justify-start'}`}>
                                            {!msg.is_admin && (
                                                <div className="h-8 w-8 rounded-full bg-[#0c1b44]/10 text-[#0c1b44] flex items-center justify-center font-bold text-sm mr-2 flex-shrink-0 self-end">
                                                    {activeSession.customer_name.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <div className={`max-w-[70%] rounded-2xl px-4 py-3 shadow-sm text-sm ${
                                                msg.is_admin
                                                    ? 'bg-[#0c1b44] text-white rounded-tr-none'
                                                    : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none'
                                            }`}>
                                                <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
                                                <div className={`text-[10px] mt-1.5 flex items-center gap-1 ${msg.is_admin ? 'text-white/60 justify-end' : 'text-gray-400 justify-start'}`}>
                                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    {msg.is_admin && <CheckCircle2 className="h-3 w-3" />}
                                                </div>
                                            </div>
                                            {msg.is_admin && (
                                                <div className="h-8 w-8 rounded-full bg-[#0c1b44] text-white flex items-center justify-center font-bold text-sm ml-2 flex-shrink-0 self-end">
                                                    A
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </div>
                            </ScrollArea>

                            {/* Input Box */}
                            <div className="p-4 bg-white border-t border-gray-100">
                                <form onSubmit={sendAdminReply} className="flex gap-3 max-w-4xl mx-auto items-center relative">
                                    <Input
                                        value={inputMsg}
                                        onChange={(e) => setInputMsg(e.target.value)}
                                        placeholder={`Reply to ${activeSession.customer_name}...`}
                                        className="bg-gray-50 border-gray-200 focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-[#0c1b44] min-h-[48px] rounded-xl pr-16 text-sm outline-none"
                                        disabled={sending}
                                    />
                                    <Button
                                        type="submit"
                                        size="icon"
                                        className="absolute right-2 top-1.5 h-9 w-9 bg-[#0c1b44] hover:bg-[#1a2d64] text-white rounded-lg shadow-sm flex items-center justify-center"
                                        disabled={!inputMsg.trim() || sending}
                                    >
                                        <Send className="h-4 w-4 ml-0.5" />
                                    </Button>
                                </form>
                                <div className="text-right mt-1.5 text-xs text-gray-400 max-w-4xl mx-auto">
                                    <kbd className="px-1 border rounded bg-gray-50 mr-1 font-sans">Enter</kbd> to send
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center bg-gray-50/30">
                            <div className="h-24 w-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                                <MessageCircle className="h-10 w-10 text-gray-300" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-700 mb-2">No Conversation Selected</h3>
                            <p className="max-w-xs text-gray-500">Choose a conversation from the sidebar to view message history and reply.</p>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminChat;
